import { useState } from 'react'
import { IUserDetailForAdmin } from '../../dto/user/user-details-for-admin.dto';
import { UserRoleEnum, UserStatusEnum } from '../../enums';
import { UserController } from '../../lib/controllers/user.ctrl';
import { FetchState } from '../../types/common'
import { IUserRole } from '@/dto/user/user-role.dto';

export const useUserListForAdmin = () => {
    const [status, setStatus] = useState<FetchState>('none');
    const [data, setData] = useState<IUserDetailForAdmin[]>([]);
    const [hasItems, setHasItems] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchData = async (params?: {
        page?: number;
        sortBy?: [string, 'ASC' | 'DESC'][];
        search?: string;
        status?: UserStatusEnum;
    }) => {
        if (status === 'fetching') {
            return;
        }
        setStatus('fetching');
        const ctrl = new UserController();
        const result = await ctrl.getUserList({
            page: params?.page ?? currentPage,
            sortBy: params?.sortBy,
            search: params?.search,
            status: params?.status
        });
        setHasItems(Array.isArray(result.data) && !!result.data.length);
        setData(result.data);
        setTotalPages(result.meta?.totalPages ?? 1);
        setItemsPerPage(result.meta?.itemsPerPage ?? 10);
        setCurrentPage(result.meta?.currentPage ?? 1);
        setStatus('fetched');
    };

    const updateLocalUserStatus = (userId: string, status: UserStatusEnum) => {
        setData(
            data.map((user: IUserDetailForAdmin) => {
                if (user.id === userId) {
                    user.isPendingVerification = false;
                    user.status = status;
                    user.canDisable = status === UserStatusEnum.ENABLED && user.roles.some(role => role.roleName !== UserRoleEnum.ADMIN && role.roleName !== UserRoleEnum.SUPER_ADMIN);
                }
                return user;
            })
        )
    };

    const removeUserLocalRole = (userId: string, role: UserRoleEnum) => {
        setData(
            data.map((user: IUserDetailForAdmin) => {
                if (user.id === userId) {
                    user.roles = user.roles.filter(r => r.roleName !== role);
                }
                return user;
            })
        )
    };

    const addUserLocalRole = (userId: string, role: UserRoleEnum) => {
        setData(
            data.map((user: IUserDetailForAdmin) => {
                if (user.id === userId) {
                    user.roles.push({ roleName: role });
                }
                return user;
            })
        )
    };

    const approveUserRequest = async (userId: string): Promise<void> => {
        updateLocalUserStatus(userId, UserStatusEnum.ENABLED);
        const ctrl = new UserController();
        await ctrl.updateUserRequest(userId, 'approved');
    };
    const denyUserRequest = async (userId: string): Promise<void> => {
        updateLocalUserStatus(userId, UserStatusEnum.DISABLED);
        const ctrl = new UserController();
        await ctrl.updateUserRequest(userId, 'denied');
    };

    const addRoleRequest = async (userId: string, role: UserRoleEnum): Promise<void> => {
        addUserLocalRole(userId, role);
        const ctrl = new UserController();
        try {
            await ctrl.updateUserRole(userId, role, 'add');
        } catch {
            removeUserLocalRole(userId, role);
        }
    };

    const removeRoleRequest = async (userId: string, role: UserRoleEnum): Promise<void> => {
        removeUserLocalRole(userId, role);
        const ctrl = new UserController();
        try {
           await ctrl.updateUserRole(userId, role, 'remove');
        } catch {
            addUserLocalRole(userId, role);
        }
    };

    const cleanData = () => {
        setData([]);
        setStatus('none');
        setCurrentPage(1);
        setTotalPages(1);
    };

    return {
        status,
        data,
        cleanData,
        hasItems,
        fetchData,
        approveUserRequest,
        denyUserRequest,
        addRoleRequest,
        removeRoleRequest,
        currentPage,
        totalPages,
        itemsPerPage,
    };
}
